import { useEffect, useState } from "react"
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../@/components/ui/dialog"
import { Button } from "../../@/components/ui/button"
import { Input } from "../../@/components/ui/input"
import { api_endpoint } from "@/constants/constant"
import axios from "axios"
import { useAuth } from "@clerk/clerk-react"
import { Spinner } from "@/components/spinner"
import { toast } from "sonner"
import Papa from "papaparse";
import { fetchAllPaginatedItems } from "@/lib/fetchAllPaginatedItems";

function EnrollStudents({
    courseId,
    onEnrolled,
    enrolledStudentIds,
    triggerLabel = "Enroll Students",
    triggerVariant = "outline",
    triggerClassName = "",
}) {
    const [open, setOpen] = useState(false)
    const [allStudents, setAllStudents] = useState([])
    const [selectedIds, setSelectedIds] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const { getToken } = useAuth()

    useEffect(() => {
        if (open) {
            // Fetch all students
            async function fetchAllStudents() {
                setLoading(true)
                try {
                    const token = await getToken()
                    const reqConfig = {
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": `Bearer ${token}`
                        }
                    }
                    const users = await fetchAllPaginatedItems({
                        url: api_endpoint + `/user/users`,
                        token,
                        selectItems: (data) => data?.users ?? [],
                        mode: "skip",
                        pageSize: 200,
                        headers: reqConfig.headers,
                    })
                    setAllStudents(users || [])

                } catch (error) {
                    console.error("Error fetching students:", error)
                    toast.error("Error fetching students")
                    setAllStudents([])
                } finally {
                    setLoading(false)
                }
            }
            fetchAllStudents()
        }
    }, [open, getToken])

    // Filter out already enrolled students
    const notEnrolledStudents = allStudents.filter(student => !enrolledStudentIds.includes(student.id))

    // Filter by search term
    const filteredStudents = notEnrolledStudents.filter(student => {
        const name = `${student.first_name ?? ""} ${student.last_name ?? ""}`.toLowerCase()
        const email = (student.email ?? "").toLowerCase()
        const term = searchTerm.toLowerCase()
        return (
            name.includes(term) || email.includes(term)
        )
    })

    const handleSelect = (id) => {
        setSelectedIds((prev) => {
            return prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
        })
    }

    const handleEnroll = async () => {
        setLoading(true)
        try {
            const token = await getToken()
            await axios.post(
                api_endpoint + "/course/enroll/batch",
                {
                    course_id: courseId,
                    user_ids: selectedIds
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                }
            )
            if (onEnrolled) onEnrolled()
            setOpen(false)
            toast.success("Students enrolled successfully")
        } catch (error) {
            console.error("Error enrolling students:", error)
            toast.error("Error enrolling students")
        } finally {
            setLoading(false)
        }
    }

    const handleCsvUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: function (results) {
                const users = results.data;
                const emails = users.map(u =>
                    (u.email || u.Email || u.EMAIL || u.EMAILS || u.emails || u.Emails)?.trim().toLowerCase()
                ).filter(Boolean);

                // Emails in your system
                const allEmails = allStudents.map(s => s.email?.toLowerCase());
                // Emails already enrolled
                const enrolledEmails = notEnrolledStudents.map(s => s.email?.toLowerCase());

                // Emails not found
                const notFound = emails.filter(email => !allEmails.includes(email));
                // Emails already enrolled
                const alreadyEnrolled = emails.filter(email => !enrolledEmails.includes(email) && allEmails.includes(email));

                // Match emails to user IDs in notEnrolledStudents
                const matchedIds = notEnrolledStudents
                    .filter(student => emails.includes(student.email?.toLowerCase()))
                    .map(student => student.id);

                setSelectedIds(matchedIds);

                let message = `${matchedIds.length} students selected from CSV.`;
                if (notFound.length > 0) message += ` ${notFound.length} emails not found.`;
                if (alreadyEnrolled.length > 0) message += ` ${alreadyEnrolled.length} emails already enrolled.`;

                toast.success(message);
            },
            error: function (error) {
                toast.error("Failed to parse CSV");
                console.error(error);
            }
        });
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant={triggerVariant}
                    className={`w-full sm:w-auto ${triggerClassName}`}
                >
                    {triggerLabel}
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg h-auto border border-(--neutral-200)">
                <DialogHeader>
                    <DialogTitle className="font-heading text-(--neutral-900)">Enroll Students</DialogTitle>
                    <DialogDescription className="text-sm font-body text-(--neutral-700)">
                        Select students to enroll in this course. Use the search box to find students by name or email.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mb-3">
                    <Input
                        placeholder="Search students"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500)"
                    />
                </div>
                {/* <div className="space-y-1 mb-3">
                    <Input
                        type="file"
                        accept=".csv"
                        onChange={handleCsvUpload}
                        className="w-full font-body bg-(--surface-base) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500)"
                    />
                    <p className="text-xs text-(--neutral-500)">
                        Upload a CSV file with a column named <b>email</b>.
                    </p>
                </div> */}
                {loading ? (
                    <div className="flex justify-center py-6">
                        <Spinner />
                    </div>
                ) : (
                    <div className="max-h-64 overflow-y-auto space-y-2 p-3">
                        {filteredStudents.length === 0 ? (
                            <p className="text-sm font-body text-(--neutral-500)">
                                {searchTerm
                                    ? "Student not found"
                                    : "No students available for enrollment"}
                            </p>
                        ) : (
                            filteredStudents.map((student) => (
                                <label
                                    key={student.id}
                                    className="flex items-center gap-3 py-2 px-2 border-b border-(--neutral-300) text-(--neutral-800)"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(student.id)}
                                        onChange={() => handleSelect(student.id)}
                                        className="w-4 h-4 accent-(--color-accent)"
                                    />
                                    <span className="font-body text-sm leading-tight text-(--neutral-800)">
                                        {student.first_name} {student.last_name} ({student.email})
                                    </span>
                                </label>
                            )))}
                    </div>
                )}

                <DialogFooter className="sticky bottom-0">
                        <Button
                            variant="outline"
                            onClick={() => setOpen(false)}
                            disabled={loading}
                            className="">
                            Cancel
                        </Button>
        
                        <Button
                            onClick={handleEnroll}
                            variant=""
                            disabled={loading || selectedIds.length === 0}
                            className="">
                            Enroll Selected
                        </Button>
                </DialogFooter>

            </DialogContent>
        </Dialog>
    )
}

export default EnrollStudents